import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { MarkdownRenderer } from "@/agent/ui/markdown-renderer";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const MarkdownReportSchema = z.object({
  content: z.string(),
  title: z.string().optional(),
});

type MarkdownReportData = z.infer<typeof MarkdownReportSchema>;

/* ────────────────────── Component ────────────────────── */

export function MarkdownReport({
  widget,
  className,
}: AgentWidgetProps<MarkdownReportData>) {
  const { content, title } = widget.data;

  return (
    <div className={cn("w-full", className)}>
      {title && (
        <p className="mb-3 text-[13px] font-semibold text-text-primary">
          {title}
        </p>
      )}
      <div className="overflow-y-auto" style={{ maxHeight: 480 }}>
        <MarkdownRenderer content={content} />
      </div>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function MarkdownReportSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-stroke-subtle bg-surface-raised p-5",
        className,
      )}
    >
      {/* Title skeleton */}
      <Skeleton className="mb-4 h-4 w-40" />
      {/* Paragraph skeletons */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[90%]" />
        <Skeleton className="h-3 w-[80%]" />
      </div>
      <Skeleton className="mt-4 mb-3 h-3.5 w-32" />
      <div className="space-y-1.5">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex items-center gap-2">
            <Skeleton className="size-2 shrink-0 rounded-full" />
            <Skeleton
              className="h-3"
              style={{ width: `${55 + ((i * 13) % 30)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-4 space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[85%]" />
      </div>
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const markdownReportWidget = defineWidget<MarkdownReportData>({
  type: WIDGET_TYPES.MARKDOWN_REPORT,
  label: "마크다운 보고서",
  schema: MarkdownReportSchema,
  component: MarkdownReport,
  skeleton: MarkdownReportSkeleton,
  defaultLayout: "full",
  maxHeight: 480,
  aiHint: "AI가 생성한 분석 보고서를 마크다운 형식으로 렌더링",
});

registerWidget(markdownReportWidget);
